// src/pages/MetaTrader/Components/RealTimeDataService.js

class RealTimeDataService {
  constructor() {
    this.cache = new Map();
    this.subscribers = new Set();
    this.updateInterval = null;
    this.isActive = false;
    this.requestCount = {
      'Finnhub': 0,
      'CoinGecko': 0,
      'ExchangeRate': 0,
      'GoldAPI': 0,
      'FinancialModelingPrep': 0,
      'AlphaVantage': 0,
      'Binance': 0,
      'CryptoCompare': 0,
      'FreeForexAPI': 0,
      'CurrencyAPI': 0,
      'MetalsAPI': 0,
      'Polygon': 0,
      'IEXCloud': 0,
      'Twelvedata': 0
    };
    this.lastResetTime = Date.now();
    
    this.apiEndpoints = [
      {
        name: 'Finnhub',
        key: 'd37s94pr01qskrehlh10d37s94pr01qskrehlh1g',
        baseUrl: 'https://finnhub.io/api/v1',
        dailyLimit: 60,
        active: true,
        specializes: ['stock'],
        cost: 'Free (60/day)'
      },
      {
        name: 'ExchangeRate',
        key: null,
        baseUrl: 'https://api.exchangerate-api.com/v4/latest',
        dailyLimit: 1500,
        active: true,
        specializes: ['forex'],
        cost: 'Free (1500/day)'
      },
      {
        name: 'GoldAPI',
        key: null,
        baseUrl: 'https://api.gold-api.com/price',
        dailyLimit: 10000,
        active: true,
        specializes: ['metal'],
        cost: 'Free (10000/day)'
      },
      {
        name: 'CoinGecko',
        key: null,
        baseUrl: 'https://api.coingecko.com/api/v3',
        dailyLimit: 10,
        active: true,
        specializes: ['crypto'],
        cost: 'Free (10/minute)'
      },
      {
        name: 'Binance',
        key: null,
        baseUrl: 'https://api.binance.com/api/v3',
        dailyLimit: 1200,
        active: true,
        specializes: ['crypto'],
        cost: 'Free (1200/minute)',
        corsProxy: false
      },
      {
        name: 'CryptoCompare',
        key: null,
        baseUrl: 'https://min-api.cryptocompare.com/data',
        dailyLimit: 100000,
        active: true,
        specializes: ['crypto'],
        cost: 'Free (100k/month)'
      },
      {
        name: 'FreeForexAPI',
        key: null,
        baseUrl: 'https://api.fxratesapi.com/latest',
        dailyLimit: 1000,
        active: true,
        specializes: ['forex'],
        cost: 'Free (1000/month)'
      },
      {
        name: 'CurrencyAPI',
        key: null,
        baseUrl: 'https://api.currencyapi.com/v3/latest',
        dailyLimit: 300,
        active: true,
        specializes: ['forex'],
        cost: 'Free (300/month)'
      },
      {
        name: 'MetalsAPI',
        key: null,
        baseUrl: 'https://api.metals.dev/v1/latest',
        dailyLimit: 50,
        active: true,
        specializes: ['metal'],
        cost: 'Free (50/month)'
      },
      {
        name: 'Polygon',
        key: null,
        baseUrl: 'https://api.polygon.io/v2',
        dailyLimit: 5,
        active: false,
        specializes: ['stock', 'index'],
        cost: 'Free (5/minute)'
      },
      {
        name: 'IEXCloud',
        key: null,
        baseUrl: 'https://cloud.iexapis.com/stable',
        dailyLimit: 500000,
        active: false,
        specializes: ['stock'],
        cost: 'Free (500k/month)'
      },
      {
        name: 'Twelvedata',
        key: null,
        baseUrl: 'https://api.twelvedata.com',
        dailyLimit: 800,
        active: false,
        specializes: ['stock', 'forex', 'crypto', 'commodity', 'index'],
        cost: 'Free (800/day)'
      },
      {
        name: 'FinancialModelingPrep',
        key: 'W2J1fWjNVSY8fmBPtzdwptWOQUaBDjAO',
        baseUrl: 'https://financialmodelingprep.com/api/v3',
        dailyLimit: 250,
        active: false,
        specializes: ['index', 'commodity'],
        cost: 'Free (250/day)'
      },
      {
        name: 'AlphaVantage',
        key: 'E34P12SOQOSJHMY0',
        baseUrl: 'https://www.alphavantage.co/query',
        dailyLimit: 25,
        active: false,
        specializes: ['index', 'commodity'],
        cost: 'Free (25/day)'
      }
    ];

    // ✅ CRITICAL: Load cached prices from localStorage on startup
    this.loadCacheFromStorage();
    
    console.log('🚀 ENHANCED: Smart caching with localStorage persistence (24h expiry)');
  }

  // ✅ NEW: Load cache from localStorage on startup
  loadCacheFromStorage() {
    try {
      const storedCache = localStorage.getItem('mt_price_cache');
      if (storedCache) {
        const cacheData = JSON.parse(storedCache);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        
        let loadedCount = 0;
        Object.entries(cacheData).forEach(([symbol, data]) => {
          const age = now - data.timestamp;
          
          // ✅ Keep prices for 24 hours (not 1 hour!)
          if (age < twentyFourHours) {
            this.cache.set(symbol, data);
            loadedCount++;
            console.log(`✅ Cached ${symbol}: $${data.price} (${Math.round(age / 60000)} min old)`);
          }
        });
        
        console.log(`📦 Loaded ${loadedCount} cached prices from localStorage`);
      }
    } catch (error) {
      console.warn('Failed to load price cache:', error);
    }
  }

  // ✅ NEW: Save cache to localStorage
  saveCacheToStorage() {
    try {
      const cacheData = {};
      this.cache.forEach((value, key) => {
        cacheData[key] = value;
      });
      localStorage.setItem('mt_price_cache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save price cache:', error);
    }
  }

  canMakeRequest(apiName) {
    const now = Date.now();
    const hoursPassed = (now - this.lastResetTime) / (1000 * 60 * 60);
    
    if (hoursPassed >= 24) {
      Object.keys(this.requestCount).forEach(api => {
        this.requestCount[api] = 0;
      });
      this.lastResetTime = now;
    }
    
    const api = this.apiEndpoints.find(a => a.name === apiName && a.active);
    if (!api) return false;
    
    const currentCount = this.requestCount[apiName] || 0;
    return currentCount < api.dailyLimit;
  }
isPriceRealistic(symbol, price) {
  const ranges = {
    'XAUUSD': { min: 3000, max: 8000 },    // ✅ FIXED - Gold at $5,189 now
    'XAGUSD': { min: 20, max: 200 },       // ✅ FIXED - Silver at ~$32-86
    'WTI':    { min: 30, max: 200 },       // ✅ OK
    'BRENT':  { min: 35, max: 210 },       // ✅ OK
    'US500':  { min: 3000, max: 10000 },   // ✅ UPDATED
    'NAS100': { min: 8000, max: 35000 },   // ✅ UPDATED
    'US30':   { min: 20000, max: 70000 },  // ✅ UPDATED
    'BTCUSD': { min: 20000, max: 500000 }, // ✅ UPDATED
    'ETHUSD': { min: 500, max: 20000 },    // ✅ UPDATED
    'XRPUSD': { min: 0.1, max: 50 },       // ✅ FIXED - XRP can go higher
    'EURUSD': { min: 0.9, max: 1.5 },      // ✅ OK
    'GBPUSD': { min: 1.0, max: 1.8 },      // ✅ OK
    'AAPL':   { min: 100, max: 600 },      // ✅ UPDATED
    'GOOGL':  { min: 100, max: 500 },      // ✅ UPDATED
    'TSLA':   { min: 50, max: 1500 },
    'XAUUSD2': { min: 3400, max: 4000 }       // ✅ UPDATED
  };

    const range = ranges[symbol];
    if (!range) return true;
    
    return price >= range.min && price <= range.max;
  }

  getCompleteAssetList() {
    return {
       'XAUUSD': { 
        name: 'Gold vs US Dollar', category: 'Metals', type: 'metal', digits: 2, tickSize: 0.01, currentPrice: 2700,
        gold_api: 'XAU', metals_api: 'XAU', priority_apis: ['GoldAPI', 'MetalsAPI']
      },
      'XAUUSD2': { 
        name: 'Gold vs US Dollar', category: 'Metals', type: 'metal', digits: 2, 
        tickSize: 0.01, currentPrice: 3700,
        gold_api: null, metals_api: null, priority_apis: []
      },
      'XAGUSD': { 
        name: 'Silver vs US Dollar', category: 'Metals', type: 'metal', digits: 3, tickSize: 0.001, currentPrice: 32,
        gold_api: 'XAG', metals_api: 'XAG', priority_apis: ['GoldAPI', 'MetalsAPI']
      },
      'EURUSD': { 
        name: 'Euro vs US Dollar', category: 'Forex', type: 'forex', digits: 5, tickSize: 0.00001, currentPrice: 1.074,
        exchange_rate: { base: 'EUR', target: 'USD' }, 
        freeforex: { base: 'EUR', target: 'USD' },
        currencyapi: { base: 'EUR', target: 'USD' },
        priority_apis: ['ExchangeRate', 'FreeForexAPI', 'CurrencyAPI']
      },
      'GBPUSD': { 
        name: 'British Pound vs US Dollar', category: 'Forex', type: 'forex', digits: 5, tickSize: 0.00001, currentPrice: 1.272,
        exchange_rate: { base: 'GBP', target: 'USD' },
        freeforex: { base: 'GBP', target: 'USD' },
        currencyapi: { base: 'GBP', target: 'USD' },
        priority_apis: ['ExchangeRate', 'FreeForexAPI', 'CurrencyAPI']
      },
      'USDJPY': { 
        name: 'US Dollar vs Japanese Yen', category: 'Forex', type: 'forex', digits: 3, tickSize: 0.001, currentPrice: 150,
        exchange_rate: { base: 'USD', target: 'JPY' },
        freeforex: { base: 'USD', target: 'JPY' },
        priority_apis: ['ExchangeRate', 'FreeForexAPI']
      },
      'AUDUSD': { 
        name: 'Australian Dollar vs US Dollar', category: 'Forex', type: 'forex', digits: 5, tickSize: 0.00001, currentPrice: 0.659,
        exchange_rate: { base: 'AUD', target: 'USD' },
        freeforex: { base: 'AUD', target: 'USD' },
        priority_apis: ['ExchangeRate', 'FreeForexAPI']
      },
      'BTCUSD': { 
        name: 'Bitcoin vs US Dollar', category: 'Crypto', type: 'crypto', digits: 1, tickSize: 0.1, currentPrice: 120000,
        coingecko_id: 'bitcoin',
        binance_symbol: 'BTCUSDT', 
        cryptocompare: 'BTC',
        priority_apis: ['Binance', 'CryptoCompare', 'CoinGecko']
      },
      'ETHUSD': { 
        name: 'Ethereum vs US Dollar', category: 'Crypto', type: 'crypto', digits: 2, tickSize: 0.01, currentPrice: 3800,
        coingecko_id: 'ethereum',
        binance_symbol: 'ETHUSDT',
        cryptocompare: 'ETH',
        priority_apis: ['Binance', 'CryptoCompare', 'CoinGecko']
      },
      'XRPUSD': { 
        name: 'Ripple vs US Dollar', category: 'Crypto', type: 'crypto', digits: 4, tickSize: 0.0001, currentPrice: 3.0,
        coingecko_id: 'ripple',
        binance_symbol: 'XRPUSDT',
        cryptocompare: 'XRP',
        priority_apis: ['Binance', 'CryptoCompare', 'CoinGecko']
      },
      'LTCUSD': { 
        name: 'Litecoin vs US Dollar', category: 'Crypto', type: 'crypto', digits: 2, tickSize: 0.01, currentPrice: 85,
        coingecko_id: 'litecoin',
        binance_symbol: 'LTCUSDT',
        cryptocompare: 'LTC',
        priority_apis: ['Binance', 'CryptoCompare', 'CoinGecko']
      },
      'ADAUSD': { 
        name: 'Cardano vs US Dollar', category: 'Crypto', type: 'crypto', digits: 4, tickSize: 0.0001, currentPrice: 1.25,
        coingecko_id: 'cardano',
        binance_symbol: 'ADAUSDT',
        cryptocompare: 'ADA',
        priority_apis: ['Binance', 'CryptoCompare', 'CoinGecko']
      },
      'AAPL': { 
        name: 'Apple Inc', category: 'US Stocks', type: 'stock', digits: 2, tickSize: 0.01, currentPrice: 246,
        finnhub_symbol: 'AAPL',
        polygon_symbol: 'AAPL',
        iex_symbol: 'AAPL',
        twelvedata_symbol: 'AAPL',
        priority_apis: ['Finnhub', 'Polygon', 'IEXCloud', 'Twelvedata']
      },
      'GOOGL': { 
        name: 'Alphabet Inc', category: 'US Stocks', type: 'stock', digits: 2, tickSize: 0.01, currentPrice: 255,
        finnhub_symbol: 'GOOGL',
        polygon_symbol: 'GOOGL',
        iex_symbol: 'GOOGL',
        twelvedata_symbol: 'GOOGL',
        priority_apis: ['Finnhub', 'Polygon', 'IEXCloud', 'Twelvedata']
      },
      'TSLA': { 
        name: 'Tesla Inc', category: 'US Stocks', type: 'stock', digits: 2, tickSize: 0.01, currentPrice: 426,
        finnhub_symbol: 'TSLA',
        polygon_symbol: 'TSLA',
        iex_symbol: 'TSLA',
        twelvedata_symbol: 'TSLA',
        priority_apis: ['Finnhub', 'Polygon', 'IEXCloud', 'Twelvedata']
      },
      'MSFT': { 
        name: 'Microsoft Corporation', category: 'US Stocks', type: 'stock', digits: 2, tickSize: 0.01, currentPrice: 416,
        finnhub_symbol: 'MSFT',
        polygon_symbol: 'MSFT',
        iex_symbol: 'MSFT',
        twelvedata_symbol: 'MSFT',
        priority_apis: ['Finnhub', 'Polygon', 'IEXCloud', 'Twelvedata']
      },
      'NVDA': { 
        name: 'NVIDIA Corporation', category: 'US Stocks', type: 'stock', digits: 2, tickSize: 0.01, currentPrice: 119,
        finnhub_symbol: 'NVDA',
        polygon_symbol: 'NVDA',
        iex_symbol: 'NVDA',
        twelvedata_symbol: 'NVDA',
        priority_apis: ['Finnhub', 'Polygon', 'IEXCloud', 'Twelvedata']
      },
      'WTI': { 
        name: 'Crude Oil WTI', category: 'Energies', type: 'commodity', digits: 2, tickSize: 0.01, currentPrice: 70,
        fmp_symbol: 'WTIUSD', alpha_function: 'WTI', alpha_symbol: 'WTI',
        twelvedata_symbol: 'WTI', priority_apis: ['Twelvedata', 'FinancialModelingPrep', 'AlphaVantage']
      },
      'US500': { 
        name: 'S&P 500', category: 'Indices', type: 'index', digits: 2, tickSize: 0.01, currentPrice: 5735,
        fmp_symbol: '^GSPC', alpha_function: 'TIME_SERIES_DAILY', alpha_symbol: 'SPY',
        polygon_symbol: 'SPY', twelvedata_symbol: 'SPY',
        priority_apis: ['Polygon', 'Twelvedata', 'FinancialModelingPrep', 'AlphaVantage']
      },
      'NAS100': { 
        name: 'NASDAQ 100', category: 'Indices', type: 'index', digits: 2, tickSize: 0.01, currentPrice: 19846,
        fmp_symbol: '^NDX', alpha_function: 'TIME_SERIES_DAILY', alpha_symbol: 'QQQ',
        polygon_symbol: 'QQQ', twelvedata_symbol: 'NASDAQ',
        priority_apis: ['Polygon', 'Twelvedata', 'FinancialModelingPrep', 'AlphaVantage']
      },
      'US30': { 
        name: 'Dow Jones 30', category: 'Indices', type: 'index', digits: 1, tickSize: 0.1, currentPrice: 42186,
        fmp_symbol: '^DJI', alpha_function: 'TIME_SERIES_DAILY', alpha_symbol: 'DIA',
        polygon_symbol: 'DIA', twelvedata_symbol: 'DJI',
        priority_apis: ['Polygon', 'Twelvedata', 'FinancialModelingPrep', 'AlphaVantage']
      }
    };
  }

  async fetchRealTimePrice(symbol) {
    const assetInfo = this.getCompleteAssetList()[symbol];
    if (!assetInfo) {
      return this.generateRealisticPrice(symbol, null);
    }

    const priorityAPIs = assetInfo.priority_apis || [];
    console.log(`🔄 Testing APIs for ${symbol}: ${priorityAPIs.join(' → ')}`);

    // ✅ Try APIs with retry
    for (let attempt = 1; attempt <= 2; attempt++) {
      for (const apiName of priorityAPIs) {
        try {
          let result = null;

          switch (apiName) {
            case 'GoldAPI':
              if (assetInfo.gold_api && this.canMakeRequest('GoldAPI')) {
                result = await this.fetchGoldAPI(symbol, assetInfo);
              }
              break;
            case 'MetalsAPI':
              if (assetInfo.metals_api && this.canMakeRequest('MetalsAPI')) {
                result = await this.fetchMetalsAPI(symbol, assetInfo);
              }
              break;
            case 'ExchangeRate':
              if (assetInfo.exchange_rate && this.canMakeRequest('ExchangeRate')) {
                result = await this.fetchExchangeRateForex(symbol, assetInfo);
              }
              break;
            case 'FreeForexAPI':
              if (assetInfo.freeforex && this.canMakeRequest('FreeForexAPI')) {
                result = await this.fetchFreeForexAPI(symbol, assetInfo);
              }
              break;
            case 'CurrencyAPI':
              if (assetInfo.currencyapi && this.canMakeRequest('CurrencyAPI')) {
                result = await this.fetchCurrencyAPI(symbol, assetInfo);
              }
              break;
            case 'Binance':
              if (assetInfo.binance_symbol && this.canMakeRequest('Binance')) {
                result = await this.fetchBinance(symbol, assetInfo);
              }
              break;
            case 'CryptoCompare':
              if (assetInfo.cryptocompare && this.canMakeRequest('CryptoCompare')) {
                result = await this.fetchCryptoCompare(symbol, assetInfo);
              }
              break;
            case 'CoinGecko':
              if (assetInfo.coingecko_id && this.canMakeRequest('CoinGecko')) {
                result = await this.fetchCoinGeckoCrypto(symbol, assetInfo);
              }
              break;
            case 'Finnhub':
              if (assetInfo.finnhub_symbol && this.canMakeRequest('Finnhub')) {
                result = await this.fetchFinnhubStock(symbol, assetInfo);
              }
              break;
            case 'Polygon':
              if (assetInfo.polygon_symbol && this.canMakeRequest('Polygon')) {
                result = await this.fetchPolygon(symbol, assetInfo);
              }
              break;
            case 'IEXCloud':
              if (assetInfo.iex_symbol && this.canMakeRequest('IEXCloud')) {
                result = await this.fetchIEXCloud(symbol, assetInfo);
              }
              break;
            case 'Twelvedata':
              if (assetInfo.twelvedata_symbol && this.canMakeRequest('Twelvedata')) {
                result = await this.fetchTwelvedata(symbol, assetInfo);
              }
              break;
            case 'FinancialModelingPrep':
              if (assetInfo.fmp_symbol && this.canMakeRequest('FinancialModelingPrep')) {
                result = await this.fetchFinancialModelingPrep(symbol, assetInfo);
              }
              break;
            case 'AlphaVantage':
              if (assetInfo.alpha_symbol && this.canMakeRequest('AlphaVantage')) {
                result = await this.fetchAlphaVantage(symbol, assetInfo);
              }
              break;
          }

          if (result && result.price > 0) {
            if (this.isPriceRealistic(symbol, result.price)) {
              console.log(`✅ ${apiName} SUCCESS: ${symbol} = $${result.price.toFixed(assetInfo.digits)}`);
              
              // ✅ CACHE + SAVE TO LOCALSTORAGE
              const priceData = this.formatPriceData(result, assetInfo);
              this.cache.set(symbol, priceData);
              this.saveCacheToStorage();
              
              return priceData;
            } else {
              console.warn(`⚠️ ${apiName} unrealistic price for ${symbol}: $${result.price}`);
              continue;
            }
          }

        } catch (error) {
          console.warn(`❌ ${apiName} failed (attempt ${attempt}/2):`, error.message);
          if (attempt === 1) await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
      }
    }

    console.log(`📊 All APIs failed for ${symbol}, using cached/realistic fallback`);
    return this.generateRealisticPrice(symbol, assetInfo);
  }

  // ✅ ALL API METHODS (UNCHANGED FROM YOUR CODE)
  async fetchBinance(symbol, assetInfo) {
    try {
      const binanceSymbol = assetInfo.binance_symbol;
      const url = `https://api.binance.com/api/v3/ticker/price?symbol=${binanceSymbol}`;
      console.log(`📞 Binance: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.price && parseFloat(data.price) > 0) {
        this.requestCount['Binance']++;
        return { price: parseFloat(data.price), source: 'Binance' };
      }
    } catch (error) {
      console.warn(`❌ Binance failed:`, error.message);
    }
    return null;
  }

  async fetchCryptoCompare(symbol, assetInfo) {
    try {
      const cryptoSymbol = assetInfo.cryptocompare;
      const url = `https://min-api.cryptocompare.com/data/price?fsym=${cryptoSymbol}&tsyms=USD`;
      console.log(`📞 CryptoCompare: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.USD && data.USD > 0) {
        this.requestCount['CryptoCompare']++;
        return { price: data.USD, source: 'CryptoCompare' };
      }
    } catch (error) {
      console.warn(`❌ CryptoCompare failed:`, error.message);
    }
    return null;
  }

  async fetchFreeForexAPI(symbol, assetInfo) {
    try {
      const { base, target } = assetInfo.freeforex;
      const url = `https://api.fxratesapi.com/latest?base=${base}&currencies=${target}`;
      console.log(`📞 FreeForexAPI: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.rates && data.rates[target]) {
        this.requestCount['FreeForexAPI']++;
        return { price: data.rates[target], source: 'FreeForexAPI' };
      }
    } catch (error) {
      console.warn(`❌ FreeForexAPI failed:`, error.message);
    }
    return null;
  }

  async fetchCurrencyAPI(symbol, assetInfo) {
    try {
      const { base, target } = assetInfo.currencyapi;
      const url = `https://api.currencyapi.com/v3/latest?apikey=YOUR_KEY&base_currency=${base}&currencies=${target}`;
      console.log(`📞 CurrencyAPI: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.data && data.data[target] && data.data[target].value) {
        this.requestCount['CurrencyAPI']++;
        return { price: data.data[target].value, source: 'CurrencyAPI' };
      }
    } catch (error) {
      console.warn(`❌ CurrencyAPI failed:`, error.message);
    }
    return null;
  }

  async fetchMetalsAPI(symbol, assetInfo) {
    try {
      const metalSymbol = assetInfo.metals_api;
      const url = `https://api.metals.dev/v1/latest?access_key=YOUR_KEY&base=${metalSymbol}&symbols=USD`;
      console.log(`📞 MetalsAPI: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.rates && data.rates.USD) {
        this.requestCount['MetalsAPI']++;
        return { price: 1 / data.rates.USD, source: 'MetalsAPI' };
      }
    } catch (error) {
      console.warn(`❌ MetalsAPI failed:`, error.message);
    }
    return null;
  }

  async fetchPolygon(symbol, assetInfo) {
    const polygonApi = this.apiEndpoints.find(a => a.name === 'Polygon');
    if (!polygonApi || !polygonApi.active) return null;

    try {
      const polygonSymbol = assetInfo.polygon_symbol;
      const url = `${polygonApi.baseUrl}/aggs/ticker/${polygonSymbol}/prev?apikey=${polygonApi.key}`;
      console.log(`📞 Polygon: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.results && data.results[0] && data.results[0].c) {
        this.requestCount['Polygon']++;
        return { price: data.results[0].c, source: 'Polygon' };
      }
    } catch (error) {
      console.warn(`❌ Polygon failed:`, error.message);
    }
    return null;
  }

  async fetchIEXCloud(symbol, assetInfo) {
    const iexApi = this.apiEndpoints.find(a => a.name === 'IEXCloud');
    if (!iexApi || !iexApi.active) return null;

    try {
      const iexSymbol = assetInfo.iex_symbol;
      const url = `${iexApi.baseUrl}/stock/${iexSymbol}/quote?token=${iexApi.key}`;
      console.log(`📞 IEXCloud: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.latestPrice && data.latestPrice > 0) {
        this.requestCount['IEXCloud']++;
        return { price: data.latestPrice, source: 'IEXCloud' };
      }
    } catch (error) {
      console.warn(`❌ IEXCloud failed:`, error.message);
    }
    return null;
  }

  async fetchTwelvedata(symbol, assetInfo) {
    const twelveApi = this.apiEndpoints.find(a => a.name === 'Twelvedata');
    if (!twelveApi || !twelveApi.active) return null;

    try {
      const twelveSymbol = assetInfo.twelvedata_symbol;
      const url = `${twelveApi.baseUrl}/price?symbol=${twelveSymbol}&apikey=${twelveApi.key}`;
      console.log(`📞 Twelvedata: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.price && parseFloat(data.price) > 0) {
        this.requestCount['Twelvedata']++;
        return { price: parseFloat(data.price), source: 'Twelvedata' };
      }
    } catch (error) {
      console.warn(`❌ Twelvedata failed:`, error.message);
    }
    return null;
  }

  async fetchGoldAPI(symbol, assetInfo) {
    try {
      const metalSymbol = assetInfo.gold_api;
      const url = `https://api.gold-api.com/price/${metalSymbol}`;
      console.log(`📞 GoldAPI: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.price && parseFloat(data.price) > 0) {
        this.requestCount['GoldAPI']++;
        return { price: parseFloat(data.price), source: 'Gold-API' };
      }
    } catch (error) {
      console.warn(`❌ GoldAPI failed:`, error.message);
    }
    return null;
  }

  async fetchFinnhubStock(symbol, assetInfo) {
    if (!this.canMakeRequest('Finnhub')) return null;

    try {
      const url = `${this.apiEndpoints[0].baseUrl}/quote?symbol=${assetInfo.finnhub_symbol}&token=${this.apiEndpoints[0].key}`;
      console.log(`📞 Finnhub: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.c && data.c > 0) {
        this.requestCount['Finnhub']++;
        return { price: data.c, source: 'Finnhub' };
      }
    } catch (error) {
      console.warn(`❌ Finnhub failed:`, error.message);
    }
    return null;
  }

  async fetchExchangeRateForex(symbol, assetInfo) {
    try {
      const { base, target } = assetInfo.exchange_rate;
      const url = `https://api.exchangerate-api.com/v4/latest/${base}`;
      console.log(`📞 ExchangeRate: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.rates && data.rates[target]) {
        this.requestCount['ExchangeRate']++;
        return { price: data.rates[target], source: 'ExchangeRate' };
      }
    } catch (error) {
      console.warn(`❌ ExchangeRate failed:`, error.message);
    }
    return null;
  }

  async fetchCoinGeckoCrypto(symbol, assetInfo) {
    if (!this.canMakeRequest('CoinGecko')) return null;

    try {
      const url = `https://api.coingecko.com/api/v3/simple/price?ids=${assetInfo.coingecko_id}&vs_currencies=usd`;
      console.log(`📞 CoinGecko: ${symbol}`);
      
      const response = await fetch(url);
      
      if (response.status === 429) {
        console.warn(`⚠️ CoinGecko rate limited`);
        return null;
      }
      
      const data = await response.json();
      
      if (data[assetInfo.coingecko_id] && data[assetInfo.coingecko_id].usd) {
        this.requestCount['CoinGecko']++;
        return { price: data[assetInfo.coingecko_id].usd, source: 'CoinGecko' };
      }
    } catch (error) {
      console.warn(`❌ CoinGecko failed:`, error.message);
    }
    return null;
  }

  async fetchFinancialModelingPrep(symbol, assetInfo) {
    const fmpApi = this.apiEndpoints.find(a => a.name === 'FinancialModelingPrep');
    if (!fmpApi || !fmpApi.active) return null;

    try {
      const fmpSymbol = assetInfo.fmp_symbol;
      const url = `${fmpApi.baseUrl}/quote/${fmpSymbol}?apikey=${fmpApi.key}`;
      console.log(`📞 FMP: ${symbol}`);
      
      const response = await fetch(url);
      
      if (response.status === 403) {
        console.error(`❌ FMP: Invalid API key`);
        return null;
      }
      
      const data = await response.json();
      
      if (Array.isArray(data) && data[0] && data[0].price) {
        this.requestCount['FinancialModelingPrep']++;
        return { price: parseFloat(data[0].price), source: 'FMP' };
      }
    } catch (error) {
      console.warn(`❌ FMP failed:`, error.message);
    }
    return null;
  }

  async fetchAlphaVantage(symbol, assetInfo) {
    const alphaApi = this.apiEndpoints.find(a => a.name === 'AlphaVantage');
    if (!alphaApi || !alphaApi.active) return null;

    try {
      const alphaFunction = assetInfo.alpha_function;
      const alphaSymbol = assetInfo.alpha_symbol;
      const url = `${alphaApi.baseUrl}?function=${alphaFunction}&symbol=${alphaSymbol}&apikey=${alphaApi.key}`;
      console.log(`📞 AlphaVantage: ${symbol}`);
      
      const response = await fetch(url);
      const data = await response.json();
      
      let price = null;
      if (alphaFunction === 'TIME_SERIES_DAILY' && data['Time Series (Daily)']) {
        const latestDate = Object.keys(data['Time Series (Daily)'])[0];
        const latestData = data['Time Series (Daily)'][latestDate];
        price = parseFloat(latestData['4. close']);
      } else if (data['Global Quote'] && data['Global Quote']['05. price']) {
        price = parseFloat(data['Global Quote']['05. price']);
      }
      
      if (price && price > 0) {
        this.requestCount['AlphaVantage']++;
        return { price: price, source: 'AlphaVantage' };
      }
    } catch (error) {
      console.warn(`❌ AlphaVantage failed:`, error.message);
    }
    return null;
  }

  // ✅ ENHANCED: Smart fallback with 24h cache
  generateRealisticPrice(symbol, assetInfo) {
    const assets = this.getCompleteAssetList();
    const info = assetInfo || assets[symbol];
    
    if (!info) {
      return { bid: 1.0000, ask: 1.0001, price: 1.00005, source: 'fallback', isRealTime: false };
    }

    // ✅ CRITICAL: Check cache first (up to 24 hours old)
    const cachedPrice = this.cache.get(symbol);
    if (cachedPrice) {
      const age = Date.now() - cachedPrice.timestamp;
      const twentyFourHours = 24 * 60 * 60 * 1000;
      
      if (age < twentyFourHours) {
        console.log(`💾 Using cached price for ${symbol}: $${cachedPrice.price} (${Math.round(age / 60000)} min old)`);
        
        // Add realistic volatility to cached price
        const volatilityPercent = {
          'forex': 0.0005, 'metal': 0.002, 'crypto': 0.01,
          'stock': 0.005, 'commodity': 0.003, 'index': 0.002
        }[info.type] || 0.001;
        
        const volatility = cachedPrice.price * volatilityPercent;
        const change = (Math.random() - 0.5) * volatility;
        const currentPrice = Math.max(cachedPrice.price * 0.99, cachedPrice.price + change);
        const spread = this.calculateSpread(currentPrice, info.type);
        
        return {
          bid: currentPrice - (spread / 2),
          ask: currentPrice + (spread / 2),
          price: currentPrice,
          source: 'cached-realistic',
          timestamp: Date.now(),
          isRealTime: false
        };
      }
    }

    // ✅ No valid cache - use hardcoded + volatility
        // ✅ XAUUSD2: Locked dummy range 3500-3900
    if (symbol === 'XAUUSD2') {
      const dummyBase = 3500 + Math.random() * 400;
      const spread = this.calculateSpread(dummyBase, 'metal');
      return {
        bid: dummyBase - (spread / 2),
        ask: dummyBase + (spread / 2),
        price: dummyBase,
        source: 'dummy',
        timestamp: Date.now(),
        isRealTime: false
      };
    }

    // ✅ No valid cache - use hardcoded + volatility
    const basePrice = info.currentPrice || 100.0;
    const volatilityPercent = {
      'forex': 0.0005, 'metal': 0.002, 'crypto': 0.01,
      'stock': 0.005, 'commodity': 0.003, 'index': 0.002
    }[info.type] || 0.001;
    
    const volatility = basePrice * volatilityPercent;
    const change = (Math.random() - 0.5) * volatility;
    const currentPrice = Math.max(basePrice * 0.995, basePrice + change);
    const spread = this.calculateSpread(currentPrice, info.type);
    
    const priceData = {
      bid: currentPrice - (spread / 2),
      ask: currentPrice + (spread / 2),
      price: currentPrice,
      source: 'fallback-realistic',
      timestamp: Date.now(),
      isRealTime: false
    };
    
    // ✅ Cache this fallback price
    this.cache.set(symbol, priceData);
    this.saveCacheToStorage();
    
    return priceData;
  }

  formatPriceData(priceData, assetInfo) {
    const basePrice = priceData.price;
    const spread = this.calculateSpread(basePrice, assetInfo.type);
    
    return {
      bid: basePrice - (spread / 2),
      ask: basePrice + (spread / 2),
      price: basePrice,
      source: priceData.source,
      timestamp: Date.now(),
      isRealTime: true
    };
  }

  calculateSpread(price, type) {
    switch (type) {
      case 'forex': return price * 0.00002;
      case 'metal': return price * 0.0003;
      case 'commodity': return price * 0.0005;
      case 'crypto': return price * 0.001;
      case 'stock': return price * 0.0002;
      case 'index': return price * 0.0001;
      default: return price * 0.0001;
    }
  }

  startRealTimeUpdates() {
    if (this.isActive) return;
    
    console.log('🚀 Starting updates with smart API management...');
    this.isActive = true;
    
    const testSymbols = {
      confirmed_working: ['EURUSD', 'GBPUSD', 'XAUUSD', 'AAPL', 'GOOGL', 'TSLA'],
      crypto_fallbacks: ['BTCUSD', 'ETHUSD', 'XRPUSD'],
      new_apis: ['WTI', 'US500']
    };
    
    // Initial load
    const allSymbols = Object.keys(this.getCompleteAssetList());
    allSymbols.forEach(symbol => {
      const priceData = this.generateRealisticPrice(symbol, this.getCompleteAssetList()[symbol]);
      this.notifySubscribers(symbol, priceData);
    });
    
    let updateCounter = 0;
    this.updateInterval = setInterval(() => {
      const minute = updateCounter % 12;
      
      if (minute % 2 === 0) {
        this.updateAssetGroup('confirmed_working', testSymbols.confirmed_working);
      }
      
      if (minute % 3 === 0) {
        this.updateAssetGroup('crypto_fallbacks', testSymbols.crypto_fallbacks);
      }
      
      if (minute % 10 === 0) {
        this.updateAssetGroup('new_apis', testSymbols.new_apis);
      }
      
      updateCounter++;
    }, 60000);
  }

  async updateAssetGroup(groupName, symbols) {
    const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
    
    try {
      console.log(`🔄 Testing ${groupName}: ${randomSymbol}`);
      const priceData = await this.fetchRealTimePrice(randomSymbol);
      this.notifySubscribers(randomSymbol, priceData);
      
      if (priceData.isRealTime) {
        console.log(`🎯 ${groupName} SUCCESS: ${randomSymbol} = $${priceData.price?.toFixed(2)} (${priceData.source})`);
      } else {
        console.log(`📊 ${groupName} fallback: ${randomSymbol} = $${priceData.price?.toFixed(2)} (${priceData.source})`);
      }
    } catch (error) {
      console.error(`💥 Failed ${groupName} ${randomSymbol}:`, error.message);
    }
  }

  stopRealTimeUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isActive = false;
    console.log('⏹️ Real-time updates stopped');
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    console.log(`📡 Subscriber added. Total: ${this.subscribers.size}`);
    
    if (!this.isActive) {
      this.startRealTimeUpdates();
    }
  }

  // ✅ SMART UNSUBSCRIBE: Stop after 30s delay
  unsubscribe(callback) {
    this.subscribers.delete(callback);
    console.log(`📡 Subscriber removed. Remaining: ${this.subscribers.size}`);
    
    if (this.subscribers.size === 0) {
      // ✅ Wait 30 seconds before stopping (user might come back)
      setTimeout(() => {
        if (this.subscribers.size === 0) {
          console.log('⏹️ No subscribers for 30s - stopping updates to save API calls');
          this.stopRealTimeUpdates();
        }
      }, 30000);
    }
  }

  notifySubscribers(symbol, priceData) {
    this.subscribers.forEach(callback => {
      try {
        callback(symbol, priceData);
      } catch (error) {
        console.error('💥 Subscriber notification error:', error);
      }
    });
  }
}

export const realTimeDataService = new RealTimeDataService();
export default realTimeDataService;
